import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { MessageSquare, RefreshCcw, User, Mail, Phone, Euro, Calendar, MapPin, Clock, Filter, ArrowUp, ArrowDown, Users } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MissingInfoModal } from './MissingInfoModal';

interface ConversationMessage {
  role: string;
  content: string;
  datetime?: string;
}

interface Conversation {
  id: string;
  client_id: string;
  budget_eur: number;
  start_date: string;
  end_date: string;
  destination: string;
  summary: string;
  transcript: ConversationMessage[];
  closing_probability: number;
  last_updated: string;
  broker_notes: string;
  status: string;
  active: boolean;
  num_guests: number;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
    preferred_budget_eur: number;
    taste_notes: string;
  };
}

interface ClientsTableProps {
  conversations: Conversation[];
  isLoading: boolean;
  onViewChat: (conversation: Conversation) => void;
  onRefresh: () => void;
  onToggleActive: (conversationId: string, currentActive: boolean) => void;
  orderBy: 'all' | 'active' | 'paused';
  onOrderByChange: (orderBy: 'all' | 'active' | 'paused') => void;
}

export const ClientsTable = ({ 
  conversations, 
  isLoading, 
  onViewChat, 
  onRefresh,
  onToggleActive,
  orderBy,
  onOrderByChange
}: ClientsTableProps) => {
  const [selectedConversationForMissingInfo, setSelectedConversationForMissingInfo] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'default' | 'missing_info_asc' | 'missing_info_desc'>('default');
  const queryClient = useQueryClient();

  // Fetch unanswered questions count for all conversations
  const { data: unansweredCounts = {} } = useQuery({
    queryKey: ['unanswered-questions-counts', conversations.map(c => c.id)],
    queryFn: async () => {
      if (conversations.length === 0) return {};
      
      const conversationIds = conversations.map(c => c.id);
      const { data, error } = await supabase
        .from('question_answer')
        .select('conversation_id')
        .in('conversation_id', conversationIds)
        .is('answer', null);
      
      if (error) {
        console.error('Error fetching unanswered questions:', error);
        throw error;
      }
      
      // Count unanswered questions per conversation
      const counts: Record<string, number> = {};
      conversationIds.forEach(id => counts[id] = 0);
      
      data.forEach(item => {
        counts[item.conversation_id] = (counts[item.conversation_id] || 0) + 1;
      });
      
      console.log('Unanswered questions counts:', counts);
      return counts;
    },
    enabled: conversations.length > 0,
    refetchInterval: 1000 // Refresh every 1 second
  });

  // Mutation for toggling conversation status
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ conversationId, newStatus }: { conversationId: string; newStatus: boolean }) => {
      const { error } = await supabase
        .from('conversations')
        .update({ active: newStatus })
        .eq('id', conversationId);

      if (error) {
        console.error('Error updating conversation status:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Immediately invalidate and refresh both queries for faster updates
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['all-conversations'] });
      // Force immediate refresh
      onRefresh();
    },
    onError: (error) => {
      console.error('Failed to toggle conversation status:', error);
    }
  });

  const handleToggleActive = async (conversationId: string, currentActive: boolean) => {
    toggleStatusMutation.mutate({ 
      conversationId, 
      newStatus: !currentActive 
    });
  };

  const handleMissingInfoSort = () => {
    if (sortBy === 'default' || sortBy === 'missing_info_asc') {
      setSortBy('missing_info_desc');
    } else {
      setSortBy('missing_info_asc');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB'); // dd/mm/yyyy format
    } catch (e) {
      return dateString;
    }
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getAdditionalRequests = (conversation: Conversation) => {
    return conversation.clients?.taste_notes || conversation.summary || 'No additional requests';
  };

  const getMissingInfoColor = (count: number) => {
    if (count === 0) return 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300';
    if (count <= 2) return 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300';
    if (count <= 5) return 'bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border-orange-300';
    return 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300';
  };

  const getMissingInfoPulseClass = (count: number) => {
    if (count === 0) return '';
    if (count <= 2) return 'animate-pulse [animation-duration:3s]';
    if (count <= 5) return 'animate-pulse [animation-duration:2s]';
    return 'animate-pulse [animation-duration:1.5s]';
  };

  // Sort conversations based on sortBy preference
  const sortedConversations = React.useMemo(() => {
    if (sortBy === 'missing_info_desc') {
      return [...conversations].sort((a, b) => {
        const countA = unansweredCounts[a.id] || 0;
        const countB = unansweredCounts[b.id] || 0;
        return countB - countA; // Descending order (highest first)
      });
    } else if (sortBy === 'missing_info_asc') {
      return [...conversations].sort((a, b) => {
        const countA = unansweredCounts[a.id] || 0;
        const countB = unansweredCounts[b.id] || 0;
        return countA - countB; // Ascending order (lowest first)
      });
    }
    return conversations;
  }, [conversations, sortBy, unansweredCounts]);

  if (isLoading) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          <span className="ml-3 text-slate-600">Loading conversations...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/80 to-gray-50/80">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg">
                <User className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Client Conversations</h2>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-slate-600" />
                <Select value={orderBy} onValueChange={onOrderByChange}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Conversations</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="paused">Paused Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={onRefresh}
                variant="outline" 
                size="sm"
                className="flex items-center space-x-2 hover:bg-emerald-50 border-emerald-200 text-emerald-700 hover:text-emerald-800 shadow-sm"
              >
                <RefreshCcw className="h-4 w-4" />
                <span>Refresh</span>
              </Button>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 hover:bg-slate-100/80 border-b border-slate-200/50">
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700">
                  <button
                    onClick={handleMissingInfoSort}
                    className="flex items-center space-x-1 hover:text-slate-900 transition-colors"
                  >
                    <span>Missing Info</span>
                    {sortBy === 'missing_info_desc' && <ArrowDown className="h-4 w-4" />}
                    {sortBy === 'missing_info_asc' && <ArrowUp className="h-4 w-4" />}
                  </button>
                </TableHead>
                <TableHead className="font-semibold text-slate-700">Client</TableHead>
                <TableHead className="font-semibold text-slate-700">Contact</TableHead>
                <TableHead className="font-semibold text-slate-700">Actions</TableHead>
                <TableHead className="font-semibold text-slate-700">Last Contact</TableHead>
                <TableHead className="font-semibold text-slate-700">Destination</TableHead>
                <TableHead className="font-semibold text-slate-700">Budget</TableHead>
                <TableHead className="font-semibold text-slate-700">Number of Guests</TableHead>
                <TableHead className="font-semibold text-slate-700">Travel Dates</TableHead>
                <TableHead className="font-semibold text-slate-700">Additional Requests</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedConversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                    <User className="h-12 w-12 mx-auto mb-3 opacity-50 text-slate-400" />
                    <p className="text-slate-600">No conversations found</p>
                    <p className="text-sm mt-1 text-slate-500">
                      {orderBy === 'all' ? 'New client conversations will appear here' :
                       orderBy === 'active' ? 'No active conversations found' :
                       'No paused conversations found'}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                sortedConversations.map((conversation) => {
                  const additionalRequests = getAdditionalRequests(conversation);
                  const shouldTruncate = additionalRequests.length > 50;
                  const unansweredCount = unansweredCounts[conversation.id] || 0;
                  
                  return (
                    <TableRow key={conversation.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100/50">
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={conversation.active}
                            onCheckedChange={() => handleToggleActive(conversation.id, conversation.active)}
                            disabled={toggleStatusMutation.isPending}
                            className="data-[state=checked]:bg-emerald-600"
                          />
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            conversation.active 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {conversation.active ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center">
                          <button
                            onClick={() => setSelectedConversationForMissingInfo(conversation.id)}
                            className={`
                              w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold border-2
                              ${getMissingInfoColor(unansweredCount)} 
                              ${getMissingInfoPulseClass(unansweredCount)}
                              hover:scale-110 transition-all duration-200 cursor-pointer shadow-lg hover:shadow-xl
                              relative overflow-hidden
                            `}
                          >
                            <span className="relative z-10">{unansweredCount}</span>
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-slate-800">
                          {conversation.clients?.full_name || 'Unknown Client'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {conversation.clients?.email && (
                            <div className="flex items-center text-sm text-slate-600">
                              <Mail className="h-3 w-3 mr-1 text-slate-500" />
                              {conversation.clients.email}
                            </div>
                          )}
                          {conversation.clients?.phone && (
                            <div className="flex items-center text-sm text-slate-600">
                              <Phone className="h-3 w-3 mr-1 text-slate-500" />
                              {conversation.clients.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => onViewChat(conversation)}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-2 hover:bg-emerald-50 border-emerald-200 text-emerald-700 hover:text-emerald-800 shadow-sm"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span>View Chat</span>
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-slate-600" />
                          <span className="text-slate-800 text-sm">
                            {formatDate(conversation.last_updated)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-emerald-600" />
                          <span className="text-slate-800">
                            {conversation.destination || 'Not specified'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Euro className="h-4 w-4 mr-2 text-emerald-600" />
                          <span className="text-slate-800 font-semibold">
                            {conversation.budget_eur 
                              ? `€${conversation.budget_eur.toLocaleString()}`
                              : 'TBD'
                            }
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2 text-blue-600" />
                          <span className="text-slate-800">
                            {conversation.num_guests || 'Not specified'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-slate-600" />
                          <span className="text-slate-800">
                            {conversation.start_date && conversation.end_date
                              ? `${formatDate(conversation.start_date)} - ${formatDate(conversation.end_date)}`
                              : 'Not specified'
                            }
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          {shouldTruncate ? (
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <p className="text-sm text-slate-600 cursor-pointer hover:text-slate-800 transition-colors">
                                  {truncateText(additionalRequests)}
                                </p>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80 bg-white/95 backdrop-blur-sm border border-slate-200/50 shadow-xl">
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-slate-800">Additional Requests</h4>
                                  <p className="text-sm text-slate-600">{additionalRequests}</p>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            <p className="text-sm text-slate-600">
                              {additionalRequests}
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {selectedConversationForMissingInfo && (
        <MissingInfoModal
          conversationId={selectedConversationForMissingInfo}
          isOpen={!!selectedConversationForMissingInfo}
          onClose={() => setSelectedConversationForMissingInfo(null)}
        />
      )}
    </>
  );
};
